import { useState, useEffect, useCallback } from 'react';
import {
  Users, UserCheck, ShieldCheck, Monitor, Globe, Server, Database, Mail, Plus, Trash2, Loader2, Search, UserX, UserCheck as Enable,
  Crown, ScrollText, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, EmptyState, Pagination, SectionTitle, UserAvatar, formatDate, formatRelative, auditLabel, selectClass } from './common';

const tone = {
  online: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  partial: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  offline: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
  unknown: 'bg-secondary text-muted-foreground',
};
const statusText = { online: 'Aktif', partial: 'Kısmi', offline: 'Pasif', unknown: 'Bilinmiyor' };

const useDebounced = (value, ms = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

// ---------- Genel bakış ----------
const Mini = ({ icon: Icon, value, label, cls }) => (
  <Card className="p-4 flex items-center gap-4">
    <span className={`h-11 w-11 rounded-xl flex items-center justify-center ${cls}`}><Icon className="h-5 w-5" /></span>
    <div><p className="text-2xl font-semibold leading-tight">{value}</p><p className="text-sm text-muted-foreground">{label}</p></div>
  </Card>
);

export const OverviewSection = ({ stats, health, onRefreshHealth, isCheckingHealth, request, onOpenLogs }) => {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    let cancelled = false;
    request('/audit-logs?page=1&per_page=10').then((d) => { if (!cancelled) setLogs(d.logs || []); }).catch(() => { if (!cancelled) setLogs([]); });
    return () => { cancelled = true; };
  }, [request]);

  const services = [
    { key: 'backend', label: 'Backend API', icon: Server },
    { key: 'database', label: 'Veritabanı', icon: Database },
    { key: 'mail_service', label: 'IMAP / SMTP', icon: Mail },
  ];
  return (
    <div className="p-4 sm:p-6 xl:p-8" data-testid="overview-section">
      <SectionTitle title="Genel bakış" description="Sistemin anlık durumu ve son yönetim işlemleri." />
      {stats && (
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-4">
          <Mini icon={Users} value={stats.total_users} label="Toplam kullanıcı" cls="bg-blue-100 text-blue-600 dark:bg-blue-500/20" />
          <Mini icon={UserCheck} value={stats.active_users} label="Aktif hesap" cls="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20" />
          <Mini icon={ShieldCheck} value={stats.users_with_2fa} label="2FA etkin" cls="bg-violet-100 text-violet-600 dark:bg-violet-500/20" />
          <Mini icon={Globe} value={stats.users_with_ip_restriction} label="IP kısıtlı" cls="bg-amber-100 text-amber-600 dark:bg-amber-500/20" />
          <Mini icon={Monitor} value={stats.active_sessions} label="Aktif oturum" cls="bg-cyan-100 text-cyan-600 dark:bg-cyan-500/20" />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {services.map(({ key, label, icon: Icon }) => {
          const s = health?.[key]?.status || 'unknown';
          return (
            <Card key={key} className="p-4 flex items-center gap-3">
              <span className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center"><Icon className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1"><p className="font-medium">{label}</p><p className="text-xs text-muted-foreground truncate">{health?.[key]?.message || 'Kontrol ediliyor...'}</p></div>
              {isCheckingHealth ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${tone[s] || tone.unknown}`}>{statusText[s] || statusText.unknown}</span>}
            </Card>
          );
        })}
      </div>
      <div className="flex justify-end -mt-2 mb-4"><button onClick={onRefreshHealth} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><RefreshCw className="h-4 w-4" />Durumu yenile</button></div>
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold">Son işlemler</h2>
          <button onClick={onOpenLogs} className="text-sm text-primary hover:underline">Tümünü gör</button>
        </div>
        {logs === null ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          : logs.length === 0 ? <EmptyState icon={ScrollText} text="Henüz kayıt yok" />
            : <ul className="divide-y divide-border">{logs.map((l) => (
              <li key={l.id} className="px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-medium">{auditLabel(l.action)}</span>
                <span className="text-muted-foreground truncate flex-1 min-w-[120px]">{l.details}</span>
                <span className="text-muted-foreground whitespace-nowrap">{l.admin_username || l.user_email || '-'} · {formatRelative(l.created_at)}</span>
              </li>))}</ul>}
      </Card>
    </div>
  );
};

// ---------- Admin yönetimi ----------
export const AdminsSection = ({ admins, onAdd, onToggle, onDelete }) => (
  <div className="p-4 sm:p-6 xl:p-8 max-w-4xl" data-testid="admins-section">
    <SectionTitle title="Admin yönetimi" description="Yönetim paneline erişebilen hesaplar.">
      <Button onClick={onAdd} data-testid="add-admin-btn"><Plus className="h-4 w-4 mr-2" />Yeni admin</Button>
    </SectionTitle>
    <Card className="divide-y divide-border">
      {admins.length === 0 && <EmptyState icon={Users} text="Admin bulunamadı" />}
      {admins.map((a) => (
        <div key={a.id} className="p-4 flex items-center gap-3" data-testid={`admin-row-${a.id}`}>
          <UserAvatar user={{ email: a.username, display_name: a.username, is_active: a.is_active }} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium">{a.username}</p>
              {a.is_protected && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 px-2 py-0.5 text-xs"><Crown className="h-3 w-3" />Korumalı</span>}
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{a.role === 'super_admin' ? 'Süper admin' : 'Admin'}</span>
              {!a.is_active && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">Devre dışı</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">Oluşturulma: {formatDate(a.created_at)}{a.last_login ? ` · Son giriş: ${formatRelative(a.last_login)}` : ''}</p>
          </div>
          {!a.is_protected && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => onToggle(a)}>{a.is_active ? <><UserX className="h-4 w-4 mr-1" />Devre dışı bırak</> : <><Enable className="h-4 w-4 mr-1" />Etkinleştir</>}</Button>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onDelete(a)} aria-label={`${a.username} sil`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      ))}
    </Card>
  </div>
);

// ---------- IP ayarları ----------
export const IpSection = ({ ips, onAdd, onAddCurrent, onRemove, isSubmitting }) => (
  <div className="p-4 sm:p-6 xl:p-8 max-w-4xl" data-testid="ip-section">
    <SectionTitle title="IP ayarları" description="Yönetim paneline yalnızca bu adreslerden erişilebilir. Liste boşsa tüm adreslere izin verilir.">
      <Button variant="outline" onClick={onAddCurrent} disabled={isSubmitting} data-testid="add-current-ip">Mevcut IP'mi ekle</Button>
      <Button onClick={onAdd} data-testid="add-admin-ip-btn"><Plus className="h-4 w-4 mr-2" />IP ekle</Button>
    </SectionTitle>
    <Card className="divide-y divide-border">
      {ips.length === 0 && <EmptyState icon={Globe} text="Kısıt yok — tüm IP adreslerine izin veriliyor" />}
      {ips.map((ip) => (
        <div key={ip.id} className="p-4 flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center"><Globe className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <code className="font-mono text-sm">{ip.ip_address}</code>
            {ip.description && <p className="text-xs text-muted-foreground">{ip.description}</p>}
            <p className="text-xs text-muted-foreground">Ekleyen: {ip.created_by || '-'} · {formatDate(ip.created_at)}</p>
          </div>
          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onRemove(ip)} aria-label={`${ip.ip_address} kaldır`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </Card>
  </div>
);

// ---------- Oturumlar ----------
const PER_PAGE = 20;
export const SessionsSection = ({ request, onChanged }) => {
  const [data, setData] = useState({ sessions: [], total: 0 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await request(`/sessions?page=${page}&per_page=${PER_PAGE}${dq.trim() ? `&q=${encodeURIComponent(dq.trim())}` : ''}`));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [request, page, dq]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq]);

  const end = async (s) => {
    if (!window.confirm(`${s.user_email} kullanıcısının bu oturumu sonlandırılsın mı?`)) return;
    try {
      await request(`/users/${s.user_id}/sessions/${s.id}`, { method: 'DELETE' });
      toast.success('Oturum sonlandırıldı');
      load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-4 sm:p-6 xl:p-8" data-testid="sessions-section">
      <SectionTitle title="Oturumlar" description="Tüm kullanıcıların etkin oturumları." />
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kullanıcı, IP veya cihaz ara..." className={`${selectClass} w-full pl-9`} aria-label="Oturum ara" /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-3 pl-4 font-medium">Kullanıcı</th><th className="py-3 pr-3 font-medium">IP</th><th className="py-3 pr-3 font-medium hidden md:table-cell">Cihaz</th><th className="py-3 pr-3 font-medium hidden lg:table-cell">Başlangıç</th><th className="w-28" /></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5}><div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></td></tr>}
              {!loading && data.sessions.map((s) => (
                <tr key={s.id} className="border-b border-border/60" data-testid={`session-row-${s.id}`}>
                  <td className="py-3 pl-4">{s.user_email}</td>
                  <td className="py-3 pr-3 font-mono">{s.ip_address}</td>
                  <td className="py-3 pr-3 hidden md:table-cell text-muted-foreground max-w-[320px] truncate" title={s.user_agent || ''}>{s.user_agent || 'Bilinmeyen cihaz'}</td>
                  <td className="py-3 pr-3 hidden lg:table-cell text-muted-foreground whitespace-nowrap">{formatRelative(s.created_at)}</td>
                  <td className="pr-4 text-right"><Button variant="outline" size="sm" className="text-destructive" onClick={() => end(s)}>Sonlandır</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && data.sessions.length === 0 && <EmptyState icon={Monitor} text="Etkin oturum yok" />}
        </div>
        <Pagination page={page} perPage={PER_PAGE} total={data.total} onPage={setPage} noun="oturum" />
      </Card>
    </div>
  );
};

// ---------- İşlem kayıtları ----------
export const AuditSection = ({ request }) => {
  const [data, setData] = useState({ logs: [], total: 0 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(1); }, [dq]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request(`/audit-logs?page=${page}&per_page=${PER_PAGE}${dq.trim() ? `&q=${encodeURIComponent(dq.trim())}` : ''}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => toast.error(e.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, page, dq]);

  return (
    <div className="p-4 sm:p-6 xl:p-8" data-testid="audit-section">
      <SectionTitle title="İşlem kayıtları" description="Yönetici ve kullanıcı işlemlerinin denetim kaydı." />
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="İşlem, kullanıcı veya ayrıntı ara..." className={`${selectClass} w-full pl-9`} aria-label="Kayıt ara" data-testid="audit-search" /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-3 pl-4 font-medium">Zaman</th><th className="py-3 pr-3 font-medium">İşlem</th><th className="py-3 pr-3 font-medium hidden md:table-cell">Ayrıntı</th><th className="py-3 pr-3 font-medium hidden lg:table-cell">Yapan</th><th className="py-3 pr-4 font-medium hidden lg:table-cell">IP</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5}><div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></td></tr>}
              {!loading && data.logs.map((l) => (
                <tr key={l.id} className="border-b border-border/60">
                  <td className="py-3 pl-4 whitespace-nowrap text-muted-foreground">{formatDate(l.created_at)}</td>
                  <td className="py-3 pr-3 font-medium">{auditLabel(l.action)}</td>
                  <td className="py-3 pr-3 hidden md:table-cell text-muted-foreground max-w-[360px] truncate" title={l.details || ''}>{l.details}</td>
                  <td className="py-3 pr-3 hidden lg:table-cell">{l.admin_username || l.user_email || '-'}</td>
                  <td className="py-3 pr-4 hidden lg:table-cell font-mono text-muted-foreground">{l.ip_address || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && data.logs.length === 0 && <EmptyState icon={ScrollText} text="Kayıt bulunamadı" />}
        </div>
        <Pagination page={page} perPage={PER_PAGE} total={data.total} onPage={setPage} noun="kayıt" />
      </Card>
    </div>
  );
};
